import { toast, types, customerPortal } from "@vilocnv/allsetra-core";
import { selectDrawerSelectedAccountId } from "app/data/selectors";
import { ObjectsLocationsRequestDto } from "domain/objects/entities/dtos/objects-locations-request.dto";
import getObjectDataForMapAction from "domain/objects/store/actions/get-object-data-for-map.action";
import getObjectsLocationsAction from "domain/objects/store/actions/get-objects-locations.action";
import {
  clearSelectedObject,
  setSelectedObject,
  setObjectFilters
} from "domain/objects/store/objects.slice";
import { selectObjectsState } from "domain/objects/store/selectors/objects.selector";
import { selectGeozonesState } from "domain/geozones/store/selectors/geozones.selector";
import { useAppDispatch, useAppSelector } from "hooks";
import { useCallback, useEffect, useState } from "react";
import { getGeozonesAction } from "domain/geozones/store/actions/get-geozones.action";
import { SignalRService } from "app/data/services";
import onObjectLocationReportedEventAction from "domain/objects/store/actions/on-object-location-reported-event.action";
import onObjectIconUpdatedEventAction from "domain/objects/store/actions/on-object-icon-updated-event.action";
import { selectGroupsState } from "domain/groups/store/selectors/groups.selector";
import { selectObjectTypesState } from "domain/object-types/store/selectors/object-types.selector";
import { selectKeysState } from "domain/keys/store/selectors/keys.selector";
import { getKeysAction } from "domain/keys/store/actions/get-keys.action";
import { getGroupsAction } from "domain/groups/store/actions/get-groups.action";
import { getObjectTypesAction } from "domain/object-types/store/actions/get-object-types.action";
import { FormikHelpers } from "formik";
import { handleIsPrivateRide } from "app/data/helpers/objectsHelpers";

type ObjectForMapRequestDto = customerPortal.objects.ObjectForMapRequestDto;
type GetGeozonesRequestDto = customerPortal.geozones.GetGeozonesRequestDto;

const useDashboardMapPage = () => {
  const dispatch = useAppDispatch();
  const accountId = useAppSelector(selectDrawerSelectedAccountId);
  const [mapRef, setMapRef] = useState<google.maps.Map>();
  const [showClusters, setShowClusters] = useState(true);
  const [showSatelliteMode, setShowSatelliteMode] = useState(false);
  const [showTraffic, setShowTraffic] = useState(false);
  const [showGeozones, setShowGeozones] = useState(true);
  const [selectedTooltipMarker, setSelectedTooltipMarker] =
    useState<types.MarkerType>();
  const [selectedCluster, setSelectedCluster] = useState<any>();
  const [mounted, setMounted] = useState(false);
  const [modalFilterOpen, setModalFilterOpen] = useState(false);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const { geozones } = useAppSelector(selectGeozonesState);
  const { groups, isLoadingGroups } = useAppSelector(selectGroupsState);
  const { objectTypes, isLoadingObjectTypes } = useAppSelector(
    selectObjectTypesState
  );
  const { keys, isLoadingKeys } = useAppSelector(selectKeysState);
  const {
    objects,
    markers,
    isLoadingObjects,
    isLoadingObjectDetail,
    objectFilters,
    selectedObject
  } = useAppSelector(selectObjectsState);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;

    if (!accountId) {
      toast.info("Kindly select an account from the navigation drawer.");
      return;
    }

    SignalRService.hubConnection?.on("EventRaised", liveUpdateObject);

    dispatchObjects(objectFilters);
    dispatchGeozones();
    dispatchGroups();
    dispatchKeys();
    dispatchObjectTypes();

    return () => {
      SignalRService.hubConnection?.off("EventRaised", liveUpdateObject);
    };
  }, [mounted, accountId, objectFilters]);

  const liveUpdateObject = useCallback(
    (event: any) => {
      if (modalFilterOpen || isSelectOpen) return;

      const pzSwitchMetadata = selectedObject
        ? handleIsPrivateRide(selectedObject)
        : undefined;
      if (pzSwitchMetadata?.value === false) return;

      switch (event?.eventName) {
        case types.BackendEventsEnum.ObjectLocationReportedEvent:
          dispatch(
            onObjectLocationReportedEventAction({
              objectLocationEventData: event,
              accountId
            })
          );
          break;
        case types.BackendEventsEnum.ObjectIconUpdatedEvent:
          dispatch(
            onObjectIconUpdatedEventAction({
              objectIconUpdatedEventData: event
            })
          );
          break;

        default:
          break;
      }
    },
    [dispatch, accountId, modalFilterOpen, isSelectOpen, selectedObject]
  );

  const dispatchObjects = async (filters: types.IMapFilter) => {
    const requestData: ObjectsLocationsRequestDto = {
      accountId: accountId ?? "",
      filters: {
        ...filters
      }
    };

    return dispatch(getObjectsLocationsAction(requestData));
  };

  const dispatchGeozones = () => {
    const requestData: GetGeozonesRequestDto = {
      accountId: accountId ?? ""
    };

    dispatch(getGeozonesAction(requestData));
  };

  const dispatchKeys = () => {
    const requestData: GetGeozonesRequestDto = {
      accountId: accountId ?? ""
    };

    dispatch(getKeysAction(requestData));
  };

  const dispatchGroups = () => {
    const requestData: GetGeozonesRequestDto = {
      accountId: accountId ?? ""
    };

    dispatch(getGroupsAction(requestData));
  };

  const dispatchObjectTypes = () => {
    const requestData: GetGeozonesRequestDto = {
      accountId: accountId ?? ""
    };

    dispatch(getObjectTypesAction(requestData));
  };

  const handleMapLoad = (map: google.maps.Map) => {
    if (map) {
      setMapRef(map);
    }
  };

  const handleMarkerMouseOver = (uniqueId: string) => {
    setSelectedTooltipMarker(undefined);
    const marker = markers.find((m) => m?.uniqueId === uniqueId);
    if (marker && marker.position && marker.name) {
      setSelectedTooltipMarker(marker);
    }
  };

  const handleMarkerMouseOut = () => {
    setSelectedTooltipMarker(undefined);
  };

  const handleShowInfoWindowClick = (uniqueId: string) => {
    if (!uniqueId) return;

    dispatch(setSelectedObject({ uniqueId }));

    const requestData: ObjectForMapRequestDto = {
      accountId: accountId ?? "",
      objectId: uniqueId
    };
    dispatch(getObjectDataForMapAction(requestData));
  };

  const handleCloseInfoWindowClick = () => {
    dispatch(clearSelectedObject());
  };

  const handleClusterClick = (cluster: any) => {
    setSelectedCluster(cluster);
  };

  const handleClusterCloseInfoWindowClick = () => {
    setSelectedCluster(undefined);
  };

  const handleShowSatelliteButtonClick = () => {
    setShowSatelliteMode(!showSatelliteMode);
  };

  const handleShowClustersButtonClick = () => {
    setShowClusters(!showClusters);
  };

  const handleShowTrafficButtonClick = () => {
    setShowTraffic(!showTraffic);
  };

  const handleShowGeozonesButtonClick = () => {
    setShowGeozones(!showGeozones);
  };

  const handleFilterModalCloseClick = () => {
    setModalFilterOpen(!modalFilterOpen);
  };

  const handleFilterModalOpenClick = () => {
    setModalFilterOpen(!modalFilterOpen);
  };

  const handleModalFilterFormSubmit = async (
    values: types.IMapFilter,
    formikHelpers: FormikHelpers<types.IMapFilter>
  ) => {
    formikHelpers.setSubmitting(true);

    const { type } = await dispatchObjects(values);

    if (type === "objects/v2/getObjectsLocation/fulfilled") {
      formikHelpers.setSubmitting(false);
      setModalFilterOpen(false);
      dispatch(setObjectFilters(values));
    }
  };

  const handleFilterChange = (value: string) => {
    if (!value) return;

    const objectIndex = objects.findIndex((obj) => obj.uniqueId === value);
    if (objectIndex < 0) return;

    mapRef?.setZoom(14);
    mapRef?.panTo({
      lat: objects[objectIndex].location.latitude,
      lng: objects[objectIndex].location.longitude
    });

    handleShowInfoWindowClick(value);
  };

  const pzSwitchMetadata = selectedObject
    ? handleIsPrivateRide(selectedObject)
    : undefined;

  return {
    objectFilters,
    markers,
    showClusters,
    showSatelliteMode,
    showTraffic,
    geozones,
    showGeozones,
    groups,
    isLoadingGroups,
    keys,
    isLoadingKeys,
    objectTypes,
    isLoadingObjectTypes,
    isLoadingObjects,
    isLoadingObjectDetail,
    selectedTooltipMarker,
    selectedObject,
    selectedCluster,
    modalFilterOpen,
    isSelectOpen,
    pzSwitchMetadata,
    setIsSelectOpen,
    handleMapLoad,
    handleMarkerMouseOver,
    handleMarkerMouseOut,
    handleShowInfoWindowClick,
    handleCloseInfoWindowClick,
    handleClusterClick,
    handleClusterCloseInfoWindowClick,
    handleShowSatelliteButtonClick,
    handleShowClustersButtonClick,
    handleShowTrafficButtonClick,
    handleShowGeozonesButtonClick,
    handleFilterModalCloseClick,
    handleFilterModalOpenClick,
    handleModalFilterFormSubmit,
    handleFilterChange
  };
};

export default useDashboardMapPage;
